import {
  nowToDateNumber,
  nowToIsoDate,
  ZonedDateTimeFactory,
  tomorrowAsFormattedIsoDate,
  zonedAddDuration,
  ZonedDateTimeFormatter,
} from '@csp/csp-common-date-util';
import { EnumUtil } from '@csp/csp-common-enum-util';
import { Maybe, Patient, StateAssert, UserId, VisitLocationType, VisitStatusType } from '@csp/csp-common-model';
import { ScheduleRequestRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import {
  AppointmentCriteriaFieldV1,
  AppointmentDateRangeCriteriaFieldV1,
  AppointmentQueryCriterionV1,
  AppointmentQueryDateRangeCriterionV1,
  AppointmentQueryOperatorV1,
  AppointmentQuerySortOrderV1,
  AppointmentQueryTypeV1,
  AppointmentQueryV1,
  AppointmentSortFieldV1,
  LocationTypeV1,
} from '@csp/dmdp-api-appointment-dto';
import { groupBy, keys, uniq } from 'lodash';
import { BookedVisitDateRangeCriteriaIn } from '../model/query/BookedVisitDateRangeCriteriaIn';
import { BookedVisitDateRangeCriteriaType } from '../model/query/BookedVisitDateRangeCriteriaType';
import { BookedVisitQueryIn } from '../model/query/BookedVisitQueryIn';
import { VisitCustomStatusType } from '../model/VisitCustomStatusType';
import { RequireConfirmation } from '../model/query/RequireConfirmation';
import { DEFAULT_UNSCHEDULED_REQUEST_REF } from '../schedule/type/DefaultScheduleCodes';
import { VisitRequest } from '../schedule/model/VisitRequest';

const createAppointmentIdCriterion = (appointmentIds: Maybe<string[]>): Maybe<AppointmentQueryCriterionV1> =>
  appointmentIds?.length
    ? {
        fieldCriterion: {
          field: AppointmentCriteriaFieldV1.APPOINTMENT_ID,
          type: AppointmentQueryTypeV1.IN,
          values: appointmentIds,
        },
      }
    : undefined;

const createParticipantCriterion = (
  userIds: Maybe<UserId[]>,
  patient?: Maybe<Patient>,
): Maybe<AppointmentQueryCriterionV1> => {
  StateAssert.isFalse(!!userIds && !!patient, 'Can not use patient and participants criteria at the same time');
  const participants = patient ? [patient.userId] : userIds;
  return participants
    ? {
        fieldCriterion: {
          field: AppointmentCriteriaFieldV1.PARTICIPANT_USER_ID,
          type: AppointmentQueryTypeV1.IN,
          values: participants,
        },
      }
    : undefined;
};

const createStatusCriterion = (statuses: Maybe<VisitStatusType[]>): Maybe<AppointmentQueryCriterionV1> =>
  statuses?.length
    ? {
        fieldCriterion: {
          field: AppointmentCriteriaFieldV1.STATUS,
          type: AppointmentQueryTypeV1.IN,
          values: statuses,
        },
      }
    : undefined;

const createLocationRefCriteria = (locationRefs: Maybe<string[]>): Maybe<AppointmentQueryCriterionV1> =>
  locationRefs?.length
    ? {
        operator: AppointmentQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: AppointmentCriteriaFieldV1.LOCATION_TYPE,
              type: AppointmentQueryTypeV1.EQ,
              values: [LocationTypeV1.LOCATION],
            },
          },
          {
            fieldCriterion: {
              field: AppointmentCriteriaFieldV1.LOCATION_REF,
              type: AppointmentQueryTypeV1.IN,
              values: locationRefs,
            },
          },
        ],
      }
    : undefined;

const createLocationTypesCriterion = (locationTypes: Maybe<VisitLocationType[]>): Maybe<AppointmentQueryCriterionV1> =>
  locationTypes?.length
    ? {
        fieldCriterion: {
          field: AppointmentCriteriaFieldV1.LOCATION_TYPE,
          type: AppointmentQueryTypeV1.IN,
          values: locationTypes,
        },
      }
    : undefined;

const createScheduleRefCriterion = (scheduleRefs: Maybe<ScheduleRequestRef[]>): Maybe<AppointmentQueryCriterionV1> => {
  const groupByScheduleCode = groupBy(scheduleRefs, ref => ref.scheduleCode);

  const criteriaGroups = keys(groupByScheduleCode).map(key => ({
    scheduleCode: key,
    requestCodes: uniq(groupByScheduleCode[key]?.map(ref => ref.requestCode)),
  }));

  const criteria = criteriaGroups.map(scheduleRef => ({
    operator: AppointmentQueryOperatorV1.AND,
    criteria: [
      {
        fieldCriterion: {
          field: AppointmentCriteriaFieldV1.REQUEST_SCHEDULE_CODE,
          type: AppointmentQueryTypeV1.EQ,
          values: [scheduleRef.scheduleCode],
        },
      },
      {
        fieldCriterion: {
          field: AppointmentCriteriaFieldV1.REQUEST_CODE,
          type: AppointmentQueryTypeV1.IN,
          values: scheduleRef.requestCodes,
        },
      },
    ],
  }));

  /**
   * Unscheduled visits booked before version 6 do not have a request ref. If we are filtering for the default request
   * ref for unscheduled visits, we need to include also those that do not have a request ref.
   */
  if (
    scheduleRefs?.some(scheduleRef => ScheduleRequestRef.isSameRequestRef(scheduleRef, DEFAULT_UNSCHEDULED_REQUEST_REF))
  ) {
    criteria.push({
      operator: AppointmentQueryOperatorV1.AND,
      criteria: [
        {
          fieldCriterion: {
            field: AppointmentCriteriaFieldV1.REQUEST_SCHEDULE_CODE,
            type: AppointmentQueryTypeV1.EXISTS,
            values: ['false'],
          },
        },
      ],
    });
  }

  return scheduleRefs?.length
    ? {
        operator: AppointmentQueryOperatorV1.OR,
        criteria,
      }
    : undefined;
};

const createRequireConfirmationCriterion = (
  requireConfirmation: Maybe<RequireConfirmation>,
  visitRequests: VisitRequest[],
): Maybe<AppointmentQueryCriterionV1> => {
  const requireConfirmationRefs = visitRequests.filter(visitRequest => visitRequest.requireConfirmation);
  const refCriteria = createScheduleRefCriterion(requireConfirmationRefs);

  const criteria =
    requireConfirmation?.confirmationStatuses?.length && refCriteria
      ? [
          {
            fieldCriterion: {
              field: AppointmentCriteriaFieldV1.START_TIMESTAMP,
              type: AppointmentQueryTypeV1.LESS_THAN_EQ,
              values: [ZonedDateTimeFormatter.toZonedDateTimeString(ZonedDateTimeFactory.now())],
            },
          },
          {
            operator: AppointmentQueryOperatorV1.OR,
            criteria: [
              {
                operator: AppointmentQueryOperatorV1.AND,
                criteria: [
                  {
                    fieldCriterion: {
                      field: AppointmentCriteriaFieldV1.CUSTOM_STATUS_TYPE,
                      type: AppointmentQueryTypeV1.EQ,
                      values: [VisitCustomStatusType.CONFIRMATION],
                    },
                  },
                  {
                    fieldCriterion: {
                      field: AppointmentCriteriaFieldV1.CUSTOM_STATUS_VALUE,
                      type: AppointmentQueryTypeV1.EQ,
                      values: requireConfirmation.confirmationStatuses,
                    },
                  },
                ],
              },
              {
                fieldCriterion: {
                  field: AppointmentCriteriaFieldV1.CUSTOM_STATUS_TYPE,
                  type: AppointmentQueryTypeV1.EXISTS,
                  values: [false],
                },
              },
            ],
          },
          refCriteria,
        ]
      : undefined;

  return criteria
    ? {
        operator: AppointmentQueryOperatorV1.AND,
        criteria,
      }
    : undefined;
};

const createAppointmentQueryDateRangeCriterionV1 = (
  bookedVisitDateRangeCriteriaIn: Maybe<BookedVisitDateRangeCriteriaIn>,
): Maybe<AppointmentQueryDateRangeCriterionV1> => {
  const now = ZonedDateTimeFactory.now();
  const today = nowToIsoDate();

  if (bookedVisitDateRangeCriteriaIn) {
    switch (bookedVisitDateRangeCriteriaIn.type) {
      case BookedVisitDateRangeCriteriaType.ONLY_UPCOMING:
        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          from: today,
          fromInclusive: true,
        };
      case BookedVisitDateRangeCriteriaType.START_DATE:
        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          fromInclusive: true,
          toInclusive: true,
          from: bookedVisitDateRangeCriteriaIn?.interval?.fromInclusive
            ? ZonedDateTimeFormatter.toIsoDateString(bookedVisitDateRangeCriteriaIn.interval.fromInclusive)
            : undefined,
          to: bookedVisitDateRangeCriteriaIn?.interval?.toInclusive
            ? ZonedDateTimeFormatter.toIsoDateString(bookedVisitDateRangeCriteriaIn.interval.toInclusive)
            : undefined,
        };
      case BookedVisitDateRangeCriteriaType.TODAY:
        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          fromInclusive: true,
          toInclusive: true,
          from: today,
          to: today,
        };

      case BookedVisitDateRangeCriteriaType.TOMORROW: {
        const tomorrow = tomorrowAsFormattedIsoDate();

        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          fromInclusive: true,
          toInclusive: true,
          from: tomorrow,
          to: tomorrow,
        };
      }
      case BookedVisitDateRangeCriteriaType.SEVEN_DAYS:
        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          fromInclusive: true,
          toInclusive: true,
          from: today,
          to: ZonedDateTimeFormatter.toIsoDateString(zonedAddDuration(now, { days: 7 })),
        };
      case BookedVisitDateRangeCriteriaType.THIRTY_DAYS:
        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          fromInclusive: true,
          toInclusive: true,
          from: today,
          to: ZonedDateTimeFormatter.toIsoDateString(zonedAddDuration(now, { days: 30 })),
        };
      case BookedVisitDateRangeCriteriaType.ALL_TIME:
        return undefined;
      default:
        return {
          field: AppointmentDateRangeCriteriaFieldV1.START_DATE,
          from: undefined,
          to: undefined,
        };
    }
  } else {
    return undefined;
  }
};

const createUpcomingBookedCriteria = (
  onlyUpcoming: boolean,
  statuses?: Maybe<VisitStatusType[]>,
): Maybe<AppointmentQueryCriterionV1> => {
  StateAssert.isFalse(!!onlyUpcoming && !!statuses, 'Can not use onlyUpcoming and statuses criteria at the same time');
  return onlyUpcoming
    ? {
        operator: AppointmentQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: AppointmentCriteriaFieldV1.START_DATE,
              type: AppointmentQueryTypeV1.GREATER_THAN_EQ,
              values: [nowToDateNumber()],
            },
          },
          {
            fieldCriterion: {
              field: AppointmentCriteriaFieldV1.STATUS,
              type: AppointmentQueryTypeV1.EQ,
              values: [VisitStatusType.BOOKED],
            },
          },
        ],
      }
    : undefined;
};

const createHbsEnabledCriterion = (
  hbsEnabled: boolean,
  visitRequests: VisitRequest[],
): Maybe<AppointmentQueryCriterionV1> =>
  hbsEnabled ? createScheduleRefCriterion(visitRequests.filter(visitRequest => visitRequest.hbs.enabled)) : undefined;

const fromBookedVisitQueryIn = (
  { criteria, dateRangeCriteria, sorting, fields }: BookedVisitQueryIn,
  visitRequests: VisitRequest[],
): AppointmentQueryV1 => {
  const toAppointmentQueryDateRangeCriterion = createAppointmentQueryDateRangeCriterionV1(dateRangeCriteria);

  const criteriaV1: AppointmentQueryCriterionV1[] = [
    createParticipantCriterion(criteria?.participantIds, criteria?.patient),
    createStatusCriterion(criteria?.statuses),
    createRequireConfirmationCriterion(criteria?.requireConfirmation, visitRequests),
    createHbsEnabledCriterion(criteria?.hbsEnabled ?? false, visitRequests),
    createScheduleRefCriterion(criteria?.scheduleRefs),
    createLocationRefCriteria(criteria?.locationRefs),
    createAppointmentIdCriterion(criteria?.visitIds),
    createLocationTypesCriterion(criteria?.locationTypes),
    createUpcomingBookedCriteria(!!criteria?.onlyUpcoming, criteria?.statuses),
  ].filter(isDefined);

  return {
    criterion: {
      criteria: criteriaV1,
      operator: AppointmentQueryOperatorV1.AND,
    },
    fields,
    dateRangeCriteria: toAppointmentQueryDateRangeCriterion ? [toAppointmentQueryDateRangeCriterion] : undefined,
    sort: sorting?.map(bookedVisitSort => ({
      field: bookedVisitSort.field,
      order: EnumUtil.fromStringOrError(bookedVisitSort.order, AppointmentQuerySortOrderV1),
    })) ?? [
      {
        field: AppointmentSortFieldV1.START_TIMESTAMP,
        order: AppointmentQuerySortOrderV1.ASC,
      },
    ],
  };
};

export const VisitQueryMapper = {
  fromBookedVisitQueryIn,
};
